if (!customElements.get('voltes-financing')) {
    customElements.define('voltes-financing', class VoltesFinancing extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.planTerms = { bnpl: [3, 4, 6, 10, 12], financing: [24, 36, 48] };
        this.currentPlan = 'bnpl';
        this.defaultMonths = 12;
        this.financingApr = 9.89;
        this.isBusy = false;
        this.variants = this.readJson('[data-financing-variants]', []);
        this.translations = this.readJson('[data-financing-translations]', {});
        this.variantSelect = this.querySelector('[data-financing-variant]');
        this.buyVariantInput = this.querySelector('[data-financing-buy-variant]');
        this.buyButton = this.querySelector('[id^="VoltesFinancingBuyButton-"]');
        this.buyButtonText = this.buyButton ? this.buyButton.querySelector('.add-to-cart-text__content') : null;
        this.acceleratedCheckout = this.querySelector('.accelerated-checkout-block');
        this.modeButtons = Array.from(this.querySelectorAll('[data-financing-mode]'));
        this.buySurface = this.querySelector('[data-financing-buy-surface]');
        this.financeSurface = this.querySelector('[data-financing-finance-surface]');
        this.openButton = this.querySelector('[data-financing-open]');
        this.teaser = this.querySelector('[data-financing-teaser]');
        this.modal = this.querySelector('[data-financing-modal]');
        this.dialog = this.querySelector('.voltes-financing__dialog');
        this.closeButtons = Array.from(this.querySelectorAll('[data-financing-close]'));
        this.planButtons = Array.from(this.querySelectorAll('[data-financing-plan]'));
        this.monthsSelect = this.querySelector('[data-financing-months]');
        this.monthsRange = this.querySelector('[data-financing-months-range]');
        this.downInput = this.querySelector('[data-financing-down]');
        this.downRange = this.querySelector('[data-financing-down-range]');
        this.percentButtons = Array.from(this.querySelectorAll('[data-financing-percent]'));
        this.monthlyOutput = this.querySelector('[data-financing-monthly]');
        this.summaryOutput = this.querySelector('[data-financing-summary]');
        this.priceOutput = this.querySelector('[data-financing-price]');
        this.financedOutput = this.querySelector('[data-financing-financed]');
        this.aprOutput = this.querySelector('[data-financing-apr]');
        this.status = this.querySelector('[data-financing-status]');
        this.footer = this.querySelector('[data-financing-footer]');
        this.submitButton = this.querySelector('[data-financing-submit]');
        this.submitLabel = this.querySelector('[data-financing-submit-label]');
        this.sectionRoot = this.closest('.shopify-section');
        this.currency = this.dataset.currency || 'CHF';
        this.locale = this.dataset.locale || 'de-CH';
        this.financedTokenVariantId = this.toInt(this.dataset.financedTokenVariantId);
        this.money = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency });
        this.moneyWhole = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency, maximumFractionDigits: 0 });
        this.handleKeydown = this.onKeydown.bind(this);
        this.handleVariantUpdate = this.onVariantUpdate.bind(this);
        this.bindEvents();
        this.populateTerms(this.defaultMonths);
        this.syncDownBounds();
        this.setMode('buy');
        this.recalculate();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.handleKeydown);
        if (this.sectionRoot) this.sectionRoot.removeEventListener('variant:update', this.handleVariantUpdate);
        this.unlockScroll();
        this.restoreModal();
      }

      bindEvents() {
        this.modeButtons.forEach((button) => button.addEventListener('click', () => this.setMode(button.dataset.financingMode)));
        if (this.variantSelect) this.variantSelect.addEventListener('change', () => this.onVariantSelect());
        if (this.openButton) this.openButton.addEventListener('click', () => this.openModal(this.openButton));
        this.closeButtons.forEach((button) => button.addEventListener('click', () => this.closeModal()));
        this.planButtons.forEach((button) => button.addEventListener('click', () => this.setPlan(button.dataset.financingPlan, true)));
        if (this.monthsSelect) this.monthsSelect.addEventListener('change', () => this.syncMonths(this.toInt(this.monthsSelect.value)));
        if (this.monthsRange) this.monthsRange.addEventListener('input', () => {
          const terms = this.currentTerms();
          const index = Math.min(Math.max(this.toInt(this.monthsRange.value), 0), terms.length - 1);
          this.syncMonths(terms[index]);
        });
        if (this.downInput) {
          this.downInput.addEventListener('input', () => this.syncDown(this.toNumber(this.downInput.value)));
          this.downInput.addEventListener('change', () => this.syncDown(this.toNumber(this.downInput.value)));
        }
        if (this.downRange) this.downRange.addEventListener('input', () => this.syncDown(this.toNumber(this.downRange.value)));
        this.percentButtons.forEach((button) => button.addEventListener('click', () => {
          const percent = this.toNumber(button.dataset.financingPercent);
          this.syncDown(Math.round(this.currentPrice() * percent / 100));
        }));
        if (this.submitButton) this.submitButton.addEventListener('click', () => this.submit());
        if (this.sectionRoot) this.sectionRoot.addEventListener('variant:update', this.handleVariantUpdate);
        document.addEventListener('keydown', this.handleKeydown);
      }

      setMode(mode) {
        const financeMode = mode === 'bnpl' || mode === 'financing';
        this.modeButtons.forEach((button) => {
          const active = button.dataset.financingMode === mode;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        if (this.buySurface) this.buySurface.hidden = financeMode;
        if (this.financeSurface) this.financeSurface.hidden = !financeMode;
        if (financeMode) this.setPlan(mode, false);
        else this.closeModal();
      }

      setPlan(plan, preserveTerm) {
        this.currentPlan = plan === 'financing' ? 'financing' : 'bnpl';
        this.planButtons.forEach((button) => {
          const active = button.dataset.financingPlan === this.currentPlan;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        const preferred = preserveTerm ? this.selectedMonths() : this.currentPlan === 'bnpl' ? 12 : 48;
        this.populateTerms(preferred);
        this.recalculate();
      }

      currentTerms() {
        return this.planTerms[this.currentPlan];
      }

      populateTerms(preferred) {
        const terms = this.currentTerms();
        const months = terms.includes(preferred) ? preferred : this.currentPlan === 'bnpl' ? 12 : 48;
        if (this.monthsSelect) {
          this.monthsSelect.replaceChildren();
          terms.forEach((term) => {
            const option = document.createElement('option');
            option.value = String(term);
            option.textContent = this.t('months_option', { months: String(term) }, `${term} months`);
            option.selected = term === months;
            this.monthsSelect.appendChild(option);
          });
        }
        if (this.monthsRange) {
          this.monthsRange.max = String(terms.length - 1);
          this.monthsRange.value = String(terms.indexOf(months));
        }
      }

      syncMonths(months) {
        const terms = this.currentTerms();
        const safeMonths = terms.includes(months) ? months : terms[0];
        if (this.monthsSelect) this.monthsSelect.value = String(safeMonths);
        if (this.monthsRange) this.monthsRange.value = String(terms.indexOf(safeMonths));
        this.recalculate();
      }

      selectedMonths() {
        const months = this.toInt(this.monthsSelect ? this.monthsSelect.value : this.defaultMonths);
        return this.currentTerms().includes(months) ? months : this.currentTerms()[0];
      }

      onVariantSelect() {
        const variant = this.selectedVariant();
        if (this.buyVariantInput && variant) {
          this.buyVariantInput.value = String(variant.id);
          this.buyVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (variant && window.history && window.location) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          window.history.replaceState({}, '', url.toString());
        }
        this.syncDownBounds();
        this.recalculate();
      }

      onVariantUpdate(event) {
        const detail = event.detail || {};
        const productId = detail.data && detail.data.productId ? String(detail.data.productId) : '';
        if (productId && productId !== String(this.dataset.productId || '')) return;
        const variant = detail.resource;
        if (variant && variant.id && this.variantSelect) this.variantSelect.value = String(variant.id);
        if (variant && variant.id && this.buyVariantInput) this.buyVariantInput.value = String(variant.id);
        this.syncDownBounds();
        this.recalculate();
      }

      selectedVariant() {
        const id = this.variantSelect ? this.variantSelect.value : this.buyVariantInput ? this.buyVariantInput.value : this.dataset.initialVariantId;
        return this.variants.find((variant) => String(variant.id) === String(id)) || null;
      }

      currentPriceCents() {
        const variant = this.selectedVariant();
        return variant && Number.isFinite(Number(variant.price)) ? Number(variant.price) : this.toInt(this.dataset.initialPriceCents);
      }

      currentPrice() {
        return this.currentPriceCents() / 100;
      }

      maxDown() {
        const percent = Math.min(Math.max(this.toNumber(this.dataset.maxDownPercent), 0), 50);
        return Math.max(0, Math.round(this.currentPrice() * percent / 100));
      }

      syncDownBounds() {
        const max = this.maxDown();
        if (this.downInput) this.downInput.max = String(max);
        if (this.downRange) this.downRange.max = String(max);
        this.syncDown(this.downInput ? this.toNumber(this.downInput.value) : 0);
      }

      syncDown(value) {
        const safe = Math.min(Math.max(this.toNumber(value), 0), this.maxDown());
        if (this.downInput) this.downInput.value = String(safe);
        if (this.downRange) this.downRange.value = String(safe);
        this.recalculate();
      }

      plan() {
        const months = this.selectedMonths();
        const price = this.currentPrice();
        const down = Math.min(Math.max(this.toNumber(this.downInput ? this.downInput.value : 0), 0), this.maxDown());
        const financed = Math.max(price - down, 0);
        const apr = this.currentPlan === 'financing' ? this.financingApr : 0;
        let monthly = financed / Math.max(months, 1);
        if (apr > 0 && financed > 0) {
          const monthlyRate = Math.pow(1 + apr / 100, 1 / 12) - 1;
          monthly = financed * monthlyRate / (1 - Math.pow(1 + monthlyRate, -months));
        }
        return { type: this.currentPlan, months, price, down, financed, apr, monthly };
      }

      recalculate() {
        const plan = this.plan();
        const available = this.isAvailable();
        if (this.monthlyOutput) this.monthlyOutput.textContent = plan.price > 0 ? this.formatMoney(plan.monthly, true) : this.t('teaser_price_pending', {}, 'Price pending');
        if (this.summaryOutput) {
          const key = plan.type === 'financing' ? 'summary_financing' : 'summary_bnpl';
          this.summaryOutput.textContent = this.t(key, { months: String(plan.months), down: this.formatMoney(plan.down, true), apr: plan.apr.toFixed(2) }, '');
        }
        if (this.priceOutput) this.priceOutput.textContent = this.formatMoney(plan.price);
        if (this.financedOutput) this.financedOutput.textContent = this.formatMoney(plan.financed);
        if (this.aprOutput) this.aprOutput.textContent = plan.apr > 0 ? `${plan.apr.toFixed(2)}%` : '0%';
        if (this.teaser) this.teaser.textContent = plan.price > 0 ? this.t('teaser_estimate', { amount: this.formatMoney(plan.monthly, true), months: String(plan.months) }, '') : this.t('teaser_price_pending', {}, '');
        const selectedPercent = plan.price > 0 ? Math.round(plan.down / plan.price * 100) : 0;
        this.percentButtons.forEach((button) => button.classList.toggle('is-active', this.toInt(button.dataset.financingPercent) === selectedPercent));
        if (!this.isBusy) this.clearStatus();
        if (this.submitButton) this.submitButton.disabled = !available || plan.price <= 0 || this.isBusy;
        if (this.openButton) this.openButton.disabled = !available || plan.price <= 0 || this.isBusy;
        this.syncBuyAvailability(available);
      }

      isAvailable() {
        const variant = this.selectedVariant();
        return Boolean(variant && variant.available !== false);
      }

      syncBuyAvailability(available) {
        if (this.buyButton) this.buyButton.disabled = !available;
        if (this.buyButtonText) this.buyButtonText.textContent = available ? this.t('add_to_cart', {}, 'Add to cart') : this.t('sold_out', {}, 'Sold out');
        if (this.acceleratedCheckout) this.acceleratedCheckout.hidden = !available;
      }

      openModal(trigger) {
        if (!this.modal || !this.modal.hidden) return;
        this.lastTrigger = trigger;
        this.mountModal();
        this.lockScroll();
        this.modal.hidden = false;
        this.recalculate();
        const focusTarget = this.modal.querySelector('[data-financing-plan].is-active') || this.modal.querySelector('[data-financing-close]');
        if (focusTarget) window.setTimeout(() => focusTarget.focus({ preventScroll: true }), 0);
      }

      closeModal() {
        if (!this.modal || this.isBusy || this.modal.hidden) return;
        this.modal.hidden = true;
        this.unlockScroll();
        this.restoreModal();
        if (this.lastTrigger) window.setTimeout(() => this.lastTrigger.focus({ preventScroll: true }), 0);
      }

      mountModal() {
        if (!this.modal || this.modal.parentNode === document.body) return;
        this.modalHome = document.createComment('voltes-financing-modal-home');
        this.modal.parentNode.insertBefore(this.modalHome, this.modal);
        document.body.appendChild(this.modal);
      }

      restoreModal() {
        if (!this.modal || !this.modalHome || !this.modalHome.parentNode) return;
        this.modalHome.parentNode.insertBefore(this.modal, this.modalHome);
        this.modalHome.remove();
        this.modalHome = null;
      }

      lockScroll() {
        if (this.scrollLock) return;
        this.scrollLock = { y: window.scrollY, overflow: document.body.style.overflow, position: document.body.style.position, top: document.body.style.top, width: document.body.style.width };
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollLock.y}px`;
        document.body.style.width = '100%';
      }

      unlockScroll() {
        if (!this.scrollLock) return;
        const lock = this.scrollLock;
        document.body.style.overflow = lock.overflow;
        document.body.style.position = lock.position;
        document.body.style.top = lock.top;
        document.body.style.width = lock.width;
        window.scrollTo(0, lock.y);
        this.scrollLock = null;
      }

      onKeydown(event) {
        if (!this.modal || this.modal.hidden) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeModal();
          return;
        }
        if (event.key !== 'Tab' || !this.dialog) return;
        const focusable = Array.from(this.dialog.querySelectorAll('button:not([disabled]), select:not([disabled]), input:not([disabled]), a[href]')).filter((element) => element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      async submit() {
        if (this.isBusy) return;
        const variant = this.selectedVariant();
        const plan = this.plan();
        if (!variant || variant.available === false) {
          this.setStatus(this.t('error_select_variant', {}, ''), 'error');
          return;
        }
        if (plan.financed <= 0) {
          this.setStatus(this.t('error_financed_amount', {}, ''), 'error');
          return;
        }
        this.setBusy(true);
        try {
          await this.removeExistingFinancing(variant.id);
          await this.addFinancingItem(variant, plan);
          window.location.assign(this.dataset.cartUrl || '/cart');
        } catch (error) {
          this.setBusy(false);
          this.setStatus(error && error.message ? error.message : this.t('error_add_request', {}, ''), 'error');
        }
      }

      financingProperties(plan, variant, usesFinancedToken) {
        const planName = plan.type === 'financing' ? this.t('plan_financing', {}, 'Financing') : this.t('plan_bnpl', {}, '0% financing');
        const properties = {
          '_Financing Marker': 'heylight',
          '_Financing Calculator': 'HeyLight estimate only; actual payment method and status are recorded in Shopify payment details.',
          '_Financing Plan': planName,
          '_Financing Term': String(plan.months),
          '_Financing APR': plan.apr > 0 ? `${plan.apr.toFixed(2)}%` : '0%',
          '_Financing Down': this.formatMoney(plan.down),
          '_Financing Price': this.formatMoney(plan.price),
          '_Financing Financed': this.formatMoney(plan.financed),
          '_Financing Monthly': this.formatMoney(plan.monthly, true)
        };
        if (usesFinancedToken) {
          properties['_Leasing Marker'] = 'token';
          properties['Leasing: Für Produkt'] = this.dataset.productTitle || '';
          properties['Leasing: Variante'] = variant && variant.title ? String(variant.title) : '';
          properties['Leasing: Product ID'] = String(this.dataset.productId || '');
          properties['Leasing: Variant ID'] = variant && variant.id ? String(variant.id) : '';
        }
        return properties;
      }

      async removeExistingFinancing(selectedVariantId) {
        const response = await fetch(this.withJson(this.dataset.cartUrl || '/cart'), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        const updates = {};
        (cart.items || []).forEach((item) => {
          const properties = item.properties || {};
          const isFinancing = properties['_Financing Marker'] || properties['_Leasing Marker'] || properties['Leasing: Marker'];
          const isSelectedVariant = String(item.variant_id || item.id) === String(selectedVariantId);
          if (isFinancing || isSelectedVariant) updates[item.key] = 0;
        });
        if (!Object.keys(updates).length) return;
        const update = await fetch(this.withJson(this.dataset.cartUpdateUrl || '/cart/update'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ updates })
        });
        if (!update.ok) throw new Error(this.t('error_clear_existing', {}, 'Unable to clear the previous financing selection.'));
      }

      async addFinancingItem(variant, plan) {
        const usesFinancedToken = plan.down > 0;
        const financedQuantity = Math.round(plan.financed);
        if (usesFinancedToken && (!this.financedTokenVariantId || financedQuantity <= 0)) {
          throw new Error(this.t('error_token_missing', {}, 'Unable to prepare the financed amount.'));
        }
        const response = await fetch(this.withJson(this.dataset.cartAddUrl || '/cart/add'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [{
              id: usesFinancedToken ? this.financedTokenVariantId : Number(variant.id),
              quantity: usesFinancedToken ? financedQuantity : 1,
              properties: this.financingProperties(plan, variant, usesFinancedToken)
            }]
          })
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || (payload && payload.status)) throw new Error((payload && (payload.description || payload.message)) || this.t('error_add_request', {}, 'Unable to add financing to the cart.'));
      }

      setBusy(busy) {
        this.isBusy = busy;
        if (this.footer) this.footer.setAttribute('aria-busy', String(busy));
        if (this.submitButton) {
          this.submitButton.disabled = busy;
          this.submitButton.classList.toggle('is-busy', busy);
          this.submitButton.setAttribute('aria-busy', String(busy));
        }
        if (this.submitLabel) this.submitLabel.textContent = busy ? this.t('submit_busy', {}, 'Preparing financing...') : this.t('submit', {}, 'Add financing to cart');
        if (busy) this.setStatus(this.t('busy_adding_to_cart', {}, ''), 'info');
        else this.recalculate();
      }

      setStatus(message, state) {
        if (!this.status) return;
        this.status.hidden = !message;
        this.status.textContent = message || '';
        this.status.dataset.state = state || 'info';
      }

      clearStatus() {
        if (!this.status) return;
        this.status.hidden = true;
        this.status.textContent = '';
        delete this.status.dataset.state;
      }

      withJson(url) {
        return String(url).endsWith('.js') ? String(url) : `${url}.js`;
      }

      readJson(selector, fallback) {
        const node = this.querySelector(selector);
        if (!node) return fallback;
        try { return JSON.parse(node.textContent); } catch (error) { return fallback; }
      }

      t(key, replacements, fallback) {
        const template = typeof this.translations[key] === 'string' ? this.translations[key] : fallback || '';
        return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => Object.prototype.hasOwnProperty.call(replacements || {}, token) ? String(replacements[token]) : '');
      }

      formatMoney(value, whole) {
        return (whole ? this.moneyWhole : this.money).format(this.toNumber(value));
      }

      toInt(value) {
        const number = parseInt(value, 10);
        return Number.isFinite(number) ? number : 0;
      }

      toNumber(value) {
        const number = Number(value);
        return Number.isFinite(number) ? number : 0;
      }
    });
  }
